import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton as MuiIconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material'
import {
  Bolt as BoltIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  AddCircleOutline as AddIcon,
  EditOutlined as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddFieldIcon,
  TextFields as TextIcon,
  Numbers as NumberIcon,
  CalendarMonth as DateIcon,
  CheckBox as CheckBoxIcon,
  DeleteOutline as DeleteIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material'
import { useToast } from '@/contexts/ToastContext'
import { RoomType, ROOM_TYPE_LABELS } from '@/types/profile'
import { toApiCategoryType, toRoomType } from '@/utils/roomType'
import { RoomSubFieldDef, SubFieldType } from '@/types/roomSubInfo'
import {
  saveRoom,
  getRoom,
  createRoomSubInfo,
  updateRoomSubInfo,
  getRoomSubInfos,
  deleteRoomSubInfo,
  deleteRoom,
} from '@/services/roomService'

// ── ルームフォーム型 ───────────────────────────────────────
interface RoomFormState {
  name: string
  type: RoomType | ''
  description: string
  eventDate: string
  expiresAt: string
  password: string
  maxParticipants: string
}

type FormErrors = Record<string, string>

const initialForm: RoomFormState = {
  name: '',
  type: '',
  description: '',
  eventDate: '',
  expiresAt: '',
  password: '',
  maxParticipants: '',
}

const CATEGORY_OPTIONS: { value: RoomType; label: string }[] = [
  { value: 'business', label: ROOM_TYPE_LABELS.business },
  { value: 'dating',   label: ROOM_TYPE_LABELS.dating },
  { value: 'job',      label: ROOM_TYPE_LABELS.job },
]

// Date → datetime-local 入力値 (YYYY-MM-DDTHH:mm)
const toDatetimeLocal = (date?: Date): string => {
  if (!date) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

// ── サブ情報タイプ定義 ─────────────────────────────────────
const SUB_TYPE_OPTIONS: {
  value: SubFieldType
  label: string
  icon: React.ReactNode
  description: string
}[] = [
  { value: 'text',     label: '文字列',          icon: <TextIcon />,     description: 'テキストを自由入力' },
  { value: 'number',   label: '数値',            icon: <NumberIcon />,   description: '数値を入力' },
  { value: 'date',     label: 'DateTime',        icon: <DateIcon />,     description: '日付・時刻を選択' },
  { value: 'checkbox', label: 'チェックボックス', icon: <CheckBoxIcon />, description: 'はい / いいえ' },
]

const SUB_TYPE_LABEL: Record<SubFieldType, string> = {
  text: '文字列', number: '数値', date: 'DateTime', checkbox: 'チェックボックス',
}

const MODAL_STEPS = ['① 項目名入力', '② タイプ選択']

const parseIsoToLocal = (value?: string | null): string => {
  if (!value) return ''
  return toDatetimeLocal(new Date(value))
}

/**
 * datetime-local の入力値をタイムゾーン付きの絶対時刻(ISO 8601)へ変換する。
 *
 * datetime-local は "2026-08-31T16:52" のようにタイムゾーンを持たない。
 * これをそのまま送ると、DB(timestamptz)側でUTCとして解釈され、
 * 日本時間で入力した値が9時間ずれて保存される（有効期限が9時間遅く効く）。
 * new Date() はタイムゾーンなしの日時をローカル時刻として解釈するため、
 * toISOString() で正しい絶対時刻になる。
 */
const toIsoFromLocalInput = (value?: string): string | undefined => {
  if (!value) return undefined
  const d = new Date(value)
  return isNaN(d.getTime()) ? undefined : d.toISOString()
}

// ── Props ─────────────────────────────────────────────────
interface RoomFormPageProps {
  mode: 'create' | 'edit'
}

// ── コンポーネント ─────────────────────────────────────────
export const RoomFormPage = ({ mode }: RoomFormPageProps) => {
  const navigate = useNavigate()
  const { roomId } = useParams<{ roomId: string }>()
  const isEditMode = mode === 'edit'
  const { showSuccess, showError } = useToast()

  // ── ルームフォーム state ──────────────────────────────────
  const [form, setForm]               = useState<RoomFormState>(initialForm)
  const [errors, setErrors]           = useState<FormErrors>({})
  const [successOpen, setSuccessOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingInitial, setIsLoadingInitial] = useState(isEditMode)
  const [roomTitle, setRoomTitle] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // ── サブ情報 state ───────────────────────────────────────
  const [subFields, setSubFields]     = useState<RoomSubFieldDef[]>([])
  const [modalOpen, setModalOpen]     = useState(false)
  const [activeStep, setActiveStep]   = useState(0)
  const [draftName, setDraftName]     = useState('')
  const [draftType, setDraftType]     = useState<SubFieldType>('text')
  const [nameError, setNameError]     = useState('')

  useEffect(() => {
    if (!isEditMode || !roomId) {
      setIsLoadingInitial(false)
      return
    }

    const initEditData = async () => {
      setIsLoadingInitial(true)
      try {
        const room = await getRoom(roomId)
        const defs = await getRoomSubInfos(roomId)
        setRoomTitle(room.roomName)
        setForm({
          name: room.roomName,
          type: toRoomType(room.categoryType),
          description: room.description ?? '',
          eventDate: parseIsoToLocal(room.eventDatetime),
          expiresAt: parseIsoToLocal(room.expirationDate),
          password: room.password ?? '',
          maxParticipants: room.maxParticipants != null ? String(room.maxParticipants) : '',
        })
        setSubFields(
          defs.map((d) => ({
            id: `sub-${d.roomSubInfoId}`,
            roomSubInfoId: d.roomSubInfoId,
            label: d.propertyName,
            type: d.propertyType,
            required: d.required,
          })),
        )
      } catch (error) {
        console.error('Failed to load room edit data:', error)
      } finally {
        setIsLoadingInitial(false)
      }
    }

    initEditData()
  }, [isEditMode, roomId])

  // ── ルームフォーム ────────────────────────────────────────
  const validate = (): boolean => {
    const newErrors: FormErrors = {}
    if (!form.name.trim()) newErrors.name = 'ルーム名を入力してください'
    if (!form.type) newErrors.type = 'カテゴリタイプを選択してください'
    if (!form.eventDate) newErrors.eventDate = '開催日時を入力してください'
    if (!form.expiresAt) {
      newErrors.expiresAt = '有効期限を入力してください'
    } else if (form.eventDate && new Date(form.expiresAt) <= new Date(form.eventDate)) {
      newErrors.expiresAt = '有効期限は開催日時より後に設定してください'
    }
    if (!form.password.trim()) newErrors.password = 'ルームパスワードを入力してください'
    if (!form.maxParticipants || Number(form.maxParticipants) < 1) {
      newErrors.maxParticipants = '参加人数を入力してください（1人以上）'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (field: keyof RoomFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => { const next = { ...prev }; delete next[field]; return next })
    }
  }

  const handleSubmit = async () => {
    if (!validate()) return
    
    setIsSubmitting(true)
    try {
      const roomRes = await saveRoom({
        roomId: isEditMode ? roomId : undefined,
        roomName: form.name.trim(),
        categoryType: toApiCategoryType(form.type),
        description: form.description.trim() || undefined,
        eventDatetime: toIsoFromLocalInput(form.eventDate),
        expirationDate: toIsoFromLocalInput(form.expiresAt) ?? form.expiresAt,
        password: form.password.trim(),
        maxParticipants: Number(form.maxParticipants),
      }) as { roomId: string }

      const targetRoomId = roomRes.roomId
      for (let i = 0; i < subFields.length; i++) {
        const field = subFields[i]
        if (field.roomSubInfoId) {
          await updateRoomSubInfo({
            roomSubInfoId: field.roomSubInfoId,
            propertyName: field.label,
            propertyType: field.type,
            required: field.required || false,
            displayOrder: i,
          })
        } else {
          await createRoomSubInfo({
            roomId: targetRoomId,
            propertyName: field.label,
            propertyType: field.type,
            required: field.required || false,
            displayOrder: i,
          })
        }
      }

      if (isEditMode) {
        const latestDefs = await getRoomSubInfos(targetRoomId)
        setSubFields(
          latestDefs.map((d) => ({
            id: `sub-${d.roomSubInfoId}`,
            roomSubInfoId: d.roomSubInfoId,
            label: d.propertyName,
            type: d.propertyType,
            required: d.required,
          })),
        )
      }

      if (!isEditMode) {
        showSuccess('ルームを作成しました！')
        navigate('/')
        return
      }
      setSuccessOpen(true)
    } catch (error) {
      console.error('Failed to save room:', error)
      const code = (error as { code?: string }).code
      if (code === 'functions/permission-denied') {
        showError(isEditMode ? 'このルームを編集する権限がありません。' : 'ルームを作成する権限がありません。')
      } else {
        showError(isEditMode ? 'ルームの更新に失敗しました。' : 'ルームの作成に失敗しました。')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteRoom = async () => {
    if (!roomId) return
    setIsDeleting(true)
    try {
      await deleteRoom(roomId)
      showSuccess('ルームを削除しました')
      navigate('/')
    } catch (error) {
      console.error('Failed to delete room:', error)
      const code = (error as { code?: string }).code
      showError(
        code === 'functions/permission-denied'
          ? 'このルームを削除する権限がありません。'
          : 'ルームの削除に失敗しました。',
      )
      setIsDeleting(false)
      setDeleteConfirmOpen(false)
    }
  }

  // ── サブ情報モーダル ──────────────────────────────────────
  const openModal = () => {
    setDraftName('')
    setDraftType('text')
    setNameError('')
    setActiveStep(0)
    setModalOpen(true)
  }

  const handleModalClose = () => setModalOpen(false)

  const handleNextStep = () => {
    if (!draftName.trim()) { setNameError('項目名を入力してください'); return }
    setNameError('')
    setActiveStep(1)
  }

  const handleAddField = () => {
    const newField: RoomSubFieldDef = {
      id:    `field-${Date.now()}`,
      label: draftName.trim(),
      type:  draftType,
    }
    setSubFields((prev) => [...prev, newField])
    setModalOpen(false)
  }

  const handleRemoveField = async (field: RoomSubFieldDef) => {
    if (field.roomSubInfoId) {
      try {
        await deleteRoomSubInfo(field.roomSubInfoId)
      } catch (error) {
        console.error('Failed to delete room sub info:', error)
        return
      }
    }
    setSubFields((prev) => prev.filter((f) => f.id !== field.id))
  }

  // ── レンダリング ──────────────────────────────────────────
  if (isLoadingInitial) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
      {/* AppBar */}
      <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ px: { xs: 1.5, sm: 2 } }}>
          <IconButton color="inherit" onClick={() => navigate('/')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BoltIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>Comyuless</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            <Typography variant="body2" fontWeight={600} color="warning.main">管理者</Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* フォーム本体 */}
      <Box component="main" sx={{ flexGrow: 1, py: { xs: 3, sm: 5 } }}>
        <Container maxWidth="sm">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {isEditMode ? 'ルーム編集' : 'ルーム作成'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode ? `「${roomTitle || form.name}」の情報を編集します` : '新しいイベントルームを作成します'}
            </Typography>
          </Box>

          {/* ── 基本情報 Paper ── */}
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, border: 1, borderColor: 'divider', mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              <TextField label="ルーム名" placeholder="例: 第11回 ビジネス交流会"
                value={form.name} onChange={(e) => handleChange('name', e.target.value)}
                error={Boolean(errors.name)} helperText={errors.name} fullWidth required />

              <TextField label="ルーム詳細"
                placeholder="例: IT企業を中心としたビジネスマッチングイベントです。"
                value={form.description} onChange={(e) => handleChange('description', e.target.value)}
                fullWidth multiline rows={4}
                inputProps={{ maxLength: 500 }} helperText={`${form.description.length} / 500`} />

              <TextField select label="カテゴリタイプ" value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
                error={Boolean(errors.type)} helperText={errors.type || 'イベントの種類を選択してください'}
                fullWidth required>
                {CATEGORY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>

              <TextField label="開催日時" type="datetime-local" value={form.eventDate}
                onChange={(e) => handleChange('eventDate', e.target.value)}
                error={Boolean(errors.eventDate)} helperText={errors.eventDate}
                fullWidth required InputLabelProps={{ shrink: true }} />

              <TextField label="有効期限" type="datetime-local" value={form.expiresAt}
                onChange={(e) => handleChange('expiresAt', e.target.value)}
                error={Boolean(errors.expiresAt)}
                helperText={errors.expiresAt || 'この日時以降はいかなるロールでも入室できなくなります'}
                fullWidth required InputLabelProps={{ shrink: true }} />

              <TextField label="ルームパスワード" type={showPassword ? 'text' : 'password'}
                placeholder="会場で参加者に開示するパスワード"
                value={form.password} onChange={(e) => handleChange('password', e.target.value)}
                error={Boolean(errors.password)}
                helperText={errors.password || '入室時に参加者が入力するパスワードです'}
                fullWidth required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MuiIconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </MuiIconButton>
                    </InputAdornment>
                  ),
                }} />

              <TextField label="参加人数（上限）" type="number" placeholder="例: 30"
                value={form.maxParticipants}
                onChange={(e) => handleChange('maxParticipants', e.target.value)}
                error={Boolean(errors.maxParticipants)}
                helperText={errors.maxParticipants || '最大参加可能人数を入力してください'}
                fullWidth required inputProps={{ min: 1 }} />

            </Box>
          </Paper>

          {/* ── サブ情報アコーディオン ── */}
          <Accordion
            elevation={0}
            sx={{ borderRadius: '12px !important', border: 1, borderColor: 'divider', mb: 3, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 3, sm: 4 }, py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AssignmentIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                <Box>
                  <Typography fontWeight={700}>ユーザーに入力させたいサブ情報</Typography>
                  <Typography variant="caption" color="text.secondary">
                    入室時にユーザーへ入力させる追加項目を定義します
                    {subFields.length > 0 && `　(${subFields.length} 項目)`}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: { xs: 3, sm: 4 }, pb: 3 }}>
              {/* 追加済み項目リスト */}
              {subFields.length > 0 ? (
                <>
                  <List disablePadding sx={{ mb: 2 }}>
                    {subFields.map((field, index) => (
                      <Box key={field.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          disablePadding
                          sx={{ py: 1.25 }}
                          secondaryAction={
                            <MuiIconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveField(field)}
                              disabled={isSubmitting}
                            >
                              <DeleteIcon fontSize="small" />
                            </MuiIconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600}>{field.label}</Typography>
                                <Chip
                                  label={SUB_TYPE_LABEL[field.type]}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      </Box>
                    ))}
                  </List>
                  <Divider sx={{ mb: 2 }} />
                </>
              ) : (
                <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                  まだ項目が追加されていません
                </Typography>
              )}

              {/* 項目追加ボタン */}
              <Button
                variant="outlined"
                startIcon={<AddFieldIcon />}
                onClick={openModal}
                sx={{ borderRadius: 2 }}
              >
                項目を追加する
              </Button>
            </AccordionDetails>
          </Accordion>

          {/* ── 送信ボタン ── */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : isEditMode ? <EditIcon /> : <AddIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ mb: 4 }}
          >
            {isSubmitting ? (isEditMode ? '更新中...' : '作成中...') : isEditMode ? 'ルームを更新する' : 'ルームを作成する'}
          </Button>

          {/* 編集時のみ: ルーム削除 */}
          {isEditMode && (
            <Box sx={{ mt: 2, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700} color="error" gutterBottom>
                このルームを削除する
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                削除すると参加者は入室できなくなります。QRコードからの入室もできなくなります。この操作は取り消せません。
              </Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={isSubmitting || isDeleting}
                sx={{ borderRadius: 2 }}
              >
                ルームを削除する
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* ── サブ情報追加モーダル ── */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>サブ情報項目の追加</DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {MODAL_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{ '& .MuiStepLabel-label': { fontSize: '0.75rem' } }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: 項目名入力 */}
          {activeStep === 0 && (
            <TextField
              label="項目名"
              placeholder="例: 趣味、参加動機、アレルギー"
              value={draftName}
              onChange={(e) => { setDraftName(e.target.value); if (nameError) setNameError('') }}
              error={Boolean(nameError)}
              helperText={nameError || 'ユーザーに表示されるラベル名です'}
              fullWidth
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleNextStep() }}
            />
          )}

          {/* Step 1: タイプ選択 */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                「<strong>{draftName}</strong>」の入力タイプを選択してください
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {SUB_TYPE_OPTIONS.map((opt) => {
                  const selected = draftType === opt.value
                  return (
                    <Box
                      key={opt.value}
                      onClick={() => setDraftType(opt.value)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 2, py: 1.5, borderRadius: 2, cursor: 'pointer',
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'primary.50' : 'background.paper',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                        {opt.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={selected ? 700 : 400}>
                          {opt.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {opt.description}
                        </Typography>
                      </Box>
                      {selected && (
                        <Chip label="選択中" size="small" color="primary" sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Box>
                  )
                })}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexDirection: 'column' }}>
          {activeStep === 0 ? (
            <Button variant="contained" fullWidth onClick={handleNextStep} sx={{ borderRadius: 2, fontWeight: 700 }}>
              次へ
            </Button>
          ) : (
            <>
              <Button variant="contained" fullWidth onClick={handleAddField} sx={{ borderRadius: 2, fontWeight: 700 }}>
                追加する
              </Button>
              <Button variant="text" fullWidth color="inherit" onClick={() => setActiveStep(0)}
                sx={{ borderRadius: 2, color: 'text.secondary' }}>
                前へ戻る
              </Button>
            </>
          )}
          <Button variant="text" fullWidth color="inherit" onClick={handleModalClose}
            sx={{ borderRadius: 2, color: 'text.disabled', fontSize: '0.75rem' }}>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* ルーム削除の確認 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>ルームの削除</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            この操作は取り消せません。
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            以下のルームを削除します。
          </Typography>
          <Typography fontWeight={700}>{roomTitle || form.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            削除後は参加者の入室・QRコードからの入室ができなくなります。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
            sx={{ borderRadius: 2 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteRoom}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {isDeleting ? '削除中...' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessOpen(false)} sx={{ width: '100%' }}>
          ルームを更新しました！
        </Alert>
      </Snackbar>
    </Box>
  )
}
